import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  LinearProgress,
  SelectChangeEvent,
  Divider,
} from '@mui/material';
import {
  CampaignFormData,
  TestProtocol, // Keep for structured_test_protocols
  TargetingCondition, // Added
} from '../types';
import TestProtocolManager from './TestProtocolManager';
import TargetingConditionsManager from './TargetingConditionsManager';
import FieldLabel from './common/FieldLabel'; // Import from common folder

interface CampaignEditFormProps {
  initialData: CampaignFormData; // Changed from formData to initialData
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => void;
  onTargetingConditionsChange: (conditions: TargetingCondition[]) => void; // Added
  onStructuredProtocolsChange: (protocols: TestProtocol[]) => void; // Added
  saving: boolean;
}

const CampaignEditForm: React.FC<CampaignEditFormProps> = ({
  initialData,
  onInputChange,
  onTargetingConditionsChange, // Added
  onStructuredProtocolsChange, // Added
  saving,
}) => {
  // Changed from formData to initialData  // Handler for TestProtocolManager (now for structured_test_protocols)
  const handleStructuredProtocolsUpdate = (updatedProtocols: TestProtocol[]) => {
    onStructuredProtocolsChange(updatedProtocols);
  };
  const [formData, setFormData] = useState<CampaignFormData>(() => {
    // Initialize state from the initialData prop, applying defaults.
    return {
      name: initialData.name ?? '',
      description: initialData.description ?? '',
      start_date: initialData.start_date ?? '',
      end_date: initialData.end_date ?? '',
      output_format: initialData.output_format ?? 'csv',
      targeting_conditions: initialData.targeting_conditions ?? [],
      structured_test_protocols: initialData.structured_test_protocols ?? [],
      retention_days: initialData.retention_days ?? 365,
      // campaign_id is not part of CampaignFormData and is not initialized here.
    };
  });
  // Synchronize local state with initialData prop when it changes
  useEffect(() => {
    setFormData({
      name: initialData.name ?? '',
      description: initialData.description ?? '',
      start_date: initialData.start_date ?? '',
      end_date: initialData.end_date ?? '',
      output_format: initialData.output_format ?? 'csv',
      targeting_conditions: initialData.targeting_conditions ?? [],
      structured_test_protocols: initialData.structured_test_protocols ?? [],
      retention_days: initialData.retention_days ?? 365,
    });
  }, [initialData]);

  return (
    <Paper sx={{ p: 3 }}>
      {saving && <LinearProgress sx={{ mb: 2 }} />}
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            label={
              <FieldLabel
                label="Campaign Name"
                tooltip="A unique and descriptive name for your data collection campaign."
              />
            }
            name="name"
            value={formData.name}
            onChange={onInputChange}
            margin="normal"
            required
            disabled={saving}
          />
        </Grid>
        {/* Empty grid item for spacing? Retained from original */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        />
        <Grid size={12}>
          <TextField
            fullWidth
            label={
              <FieldLabel
                label="Description"
                tooltip="Detailed information about the purpose and goals of this campaign."
              />
            }
            name="description"
            value={formData.description}
            onChange={onInputChange}
            margin="normal"
            multiline
            rows={3}
            disabled={saving}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            label={
              <FieldLabel
                label="Start Date"
                tooltip="The date when this campaign should begin collecting data."
              />
            }
            name="start_date"
            type="date"
            // Format date for input type="date"
            value={formData.start_date ? formData.start_date.split('T')[0] : ''}
            onChange={onInputChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled={saving}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            label={
              <FieldLabel
                label="End Date"
                tooltip="The date when this campaign should stop collecting data."
              />
            }
            name="end_date"
            type="date"
            // Format date for input type="date"
            value={formData.end_date ? formData.end_date.split('T')[0] : ''}
            onChange={onInputChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled={saving}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextField
            fullWidth
            label={
              <FieldLabel
                label="Data Retention time (days)"
                tooltip="Number of days to retain data in S3. Leave empty for indefinite retention."
              />
            }
            name="retention_days"
            type="number"
            value={formData.retention_days ?? ''} // Handle undefined case for input
            onChange={onInputChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            disabled={saving}
          />
        </Grid>
        <Grid size={12}>
          <FormControl fullWidth margin="normal" disabled={saving}>
            <InputLabel id="output-format-label">
              <FieldLabel
                label="Output Format"
                tooltip="The file format for exported data. CSV is human-readable, while Parquet is more efficient for large datasets."
              />
            </InputLabel>
            <Select
              labelId="output-format-label"
              name="output_format"
              value={formData.output_format}
              onChange={onInputChange}
              label="Output Format"
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="parquet">Parquet</MenuItem>
            </Select>
          </FormControl>
        </Grid>{' '}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />{' '}
          <TargetingConditionsManager
            conditions={formData.targeting_conditions ?? []}
            onConditionsChange={(updatedConditions: TargetingCondition[]) => {
              setFormData({ ...formData, targeting_conditions: updatedConditions });
              onTargetingConditionsChange(updatedConditions);
            }}
            disabled={saving}
          />
        </Grid>
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <TestProtocolManager
            protocols={formData.structured_test_protocols ?? []}
            onProtocolsChange={handleStructuredProtocolsUpdate}
            availableTargetingConditions={formData.targeting_conditions ?? []} // Added this line
            disabled={saving}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CampaignEditForm;
